<script setup lang="ts">
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    sortKpiViews,
    Textarea,
    ZhConfirmButton,
    ZhEmptyState,
    ZhKpiLight,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import {
    api,
    emptyCharter,
    formatConcurrentRunLimit,
    type KpiReadingView,
    type Member,
    parseConcurrentRunLimit,
    type TeamCharter,
    type TeamDetail,
    type TeamKpiTree,
    type TeamMemoryGrants,
    type TeamShareReview,
} from '@/api'
import AgentConnectionsPanel from '@/components/AgentConnectionsPanel.vue'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import KpiListEditor from '@/components/KpiListEditor.vue'
import KpiTargetCards from '@/components/KpiTargetCards.vue'
import MemberAvatar from '@/components/MemberAvatar.vue'
import TaskGraph from '@/components/TaskGraph.vue'
import TeamShareDialog from '@/components/TeamShareDialog.vue'
import ToolBindings from '@/components/ToolBindings.vue'
import { useClientTable } from '@/composables/useClientTable'
import { useFormDraft } from '@/composables/useFormDraft'
import { useListState } from '@/composables/useListState'
import { useRouteIdGuard } from '@/composables/useRouteIdGuard'
import { useVisiblePolling } from '@/composables/useVisiblePolling'
import type { Kpi } from '@/lib/kpi'
import { memberLabel } from '@/lib/memberLabel'
import { COO_ACTING_AS_LEAD, leadChoice, leadMemberIdOf } from '@/lib/teamLead'

/*
 * Every block sits on a kit surface under a kit heading, and every control, chip and empty state is the kit's
 * (#1222): the screen states no colour, radius, border or fill of its own. DataTable, MemberAvatar, TaskGraph
 * and ToolBindings are portal-wide and are migrated under their own items; here they only sit on the surfaces.
 */
const route = useRoute()
const router = useRouter()
const team = ref<TeamDetail | null>(null)
const people = ref<Member[]>([])
/**
 * The add-member picker holds no member: picking one adds them at once, so it always reads its placeholder.
 * Held as a ref so the kit Select is bound (controlled) and goes back to empty after every pick.
 */
const pickMember = ref('')
const busy = ref(false)
/**
 * The team's goal is its charter's purpose (#1844): the one line the team, the catalog and the public site show. The
 * other charter answers stay stored for what still reads them, but nothing here shows or edits them any more.
 */
const charter = ref<TeamCharter | null>(null)
const charterDraft = useFormDraft(`team-goal:${route.params.id}`, { charter })
/** The goal and KPIs sit behind "Edit team"; a restored unsaved draft opens them, so the draft is not hidden. */
const editing = ref(charter.value !== null)
/**
 * The team's normalized KPIs (#895), edited beside the goal but read and saved through their own endpoint.
 * `null` until read, and if the read fails: a save then leaves the stored KPIs as they are rather than
 * overwrite KPIs it never read.
 */
const charterKpis = ref<Kpi[] | null>(null)
const charterKpisFailed = ref(false)
/** The KPIs as last read or saved, so Save writes them only when they were edited. */
let storedKpis = ''
/**
 * Sharing the team to the catalog (#1844): what the share would publish, and the entry it has, read beside the team
 * and again after a share. Null until read, and when the read fails: the Share button still opens the dialog, which
 * reads for itself.
 */
const share = ref<TeamShareReview | null>(null)
const shareOpen = ref(false)
const shareNotice = ref('')
/** The team's KPIs as last read or saved, shown as cards: an edit in progress is not on the page until it is saved. */
const savedKpis = ref<Kpi[]>([])
const saved = ref(false)
const error = ref<string | null>(null)
const deleting = ref(false)
/** The title-row on/off switch's request is in flight: the switch is disabled and a second click sends nothing. */
const toggling = ref(false)
/** Why the last switch failed, shown beside it; never `error`, which takes the whole page's place. */
const enabledError = ref<string | null>(null)
/** The stored state, as last read or as the switch's request answered it. The column defaults on, so omitted is on. */
const teamOn = computed(() => team.value?.enabled !== false)
/**
 * Who last switched the team, and when (#1812), as one muted line under the switch: from the team read and, after a
 * click, from the switch's own answer, so it follows without a reload. Empty when the team was never switched; a
 * failed switch leaves `team` as it was, so the previous line stands.
 */
const statusChangeLine = computed(() => {
    const change = team.value?.lastStatusChange
    if (!change) return ''
    return `Switched ${change.enabled ? 'on' : 'off'} by ${change.actorName} · ${new Date(change.changedAt).toLocaleString()}`
})
// The kit `Input` with `type="number"` hands up a `number` here at runtime once the field parses, despite
// the string initial value — see `parseConcurrentRunLimit`'s doc comment in `@/api`.
const concurrentRunLimitInput = ref<string | number>('')
const concurrencyInitialized = ref(false)
const concurrencyError = ref<string | null>(null)
const workflowTasks = computed(() => (team.value?.tasks ?? []).filter(task => task.builtInRole !== 'analysis'))
/** The team's KPIs, and the role and task KPIs beneath them (#898). `null` until read, and if the read fails. */
const kpiTree = ref<TeamKpiTree | null>(null)

/** The team's own KPIs, at risk, off target and overdue first — the order every KPI list is read in. */
const teamKpis = computed(() => sortKpiViews(kpiTree.value?.kpis ?? []))

/** Each role with its KPIs and its tasks', each list in that same worst-first order. */
const roleKpis = computed(() =>
    (kpiTree.value?.roles ?? []).map(role => ({
        ...role,
        kpis: sortKpiViews(role.kpis),
        tasks: role.tasks.map(task => ({ ...task, kpis: sortKpiViews(task.kpis) })),
    })),
)

/**
 * Every KPI on the tree still waiting for sign-off: the team's own, its roles' and their tasks'. Nothing on it is
 * measured yet, so the page says how many wait and offers to sign them off in one step.
 */
const pendingKpiCount = computed(
    () =>
        [
            ...teamKpis.value,
            ...roleKpis.value.flatMap(role => [...role.kpis, ...role.tasks.flatMap(task => task.kpis)]),
        ].filter(kpi => kpi.pending).length,
)
const signingOff = ref(false)
const signOffNotice = ref('')
const signOffError = ref('')

/**
 * Signs off every KPI the team holds, its roles' and its tasks' with it, as the clone screen's last step does. A KPI
 * with no source stays waiting, since no run could record it, and the notice says how many did.
 */
async function signOffAll(): Promise<void> {
    const teamId = team.value?.id
    if (!teamId || signingOff.value) return
    signingOff.value = true
    signOffNotice.value = ''
    signOffError.value = ''
    try {
        const { signedOff } = await api.signOffTeamKpis(teamId, { edits: [] })
        if (!isCurrentRoute(teamId)) return
        await Promise.all([loadKpis(), loadKpiTargets()])
        const left = pendingKpiCount.value
        signOffNotice.value =
            `Signed off ${signedOff.length} KPI${signedOff.length === 1 ? '' : 's'}.` +
            (left > 0 ? ` ${left} still need${left === 1 ? 's' : ''} a source, or a cadence for a target, first.` : '')
    } catch (caught) {
        if (isCurrentRoute(teamId)) {
            signOffError.value =
                caught instanceof Error ? `Could not sign off (${caught.message}).` : 'Could not sign off.'
        }
    } finally {
        signingOff.value = false
    }
}

/** Trends read as words as well as arrows, so the direction is never carried by the glyph alone. */
const TREND_WORD: Readonly<Record<KpiReadingView['trend'], string>> = {
    up: '↑ rising',
    down: '↓ falling',
    flat: '→ flat',
}

/**
 * A reading's trend and when it was last read, beside its light: colour is never the only channel. A KPI never
 * read has no trend to say, only that it was never read.
 */
function readingNote(reading: KpiReadingView): string {
    const overdue = reading.overdue ? ' · overdue' : ''
    if (!reading.lastReadAt) return `never read${overdue}`
    return `${TREND_WORD[reading.trend]} · read ${new Date(reading.lastReadAt).toLocaleDateString()}${overdue}`
}
const isCurrentRoute = useRouteIdGuard()
let inFlight = false

/**
 * A silent load never blanks what is already on screen: it reassigns `team` so the graph's nodes
 * re-render in place (no Vue Flow teardown, no pan/zoom loss), and swallows a failed background
 * fetch instead of replacing a working graph with an error state.
 *
 * `isCurrentRoute()` (shared with RoleDetailView, also via `useRouteIdGuard()`) drops a response
 * that lands after the page has navigated to a different team, rather than overwriting the team
 * now on screen.
 */
async function load(options: { silent?: boolean } = {}): Promise<void> {
    if (options.silent && inFlight) {
        return
    }
    const teamId = String(route.params.id)
    inFlight = true
    try {
        const [loaded, allPeople] = await Promise.all([api.team(teamId), api.members().catch(() => [] as Member[])])
        if (!isCurrentRoute(teamId)) {
            return
        }
        team.value = loaded
        people.value = allPeople
        if (!charter.value) {
            charter.value = { ...emptyCharter(), ...loaded.charter }
        }
        if (!concurrencyInitialized.value) {
            concurrentRunLimitInput.value = formatConcurrentRunLimit(loaded.concurrentRunLimit)
            concurrencyInitialized.value = true
        }
        error.value = null
    } catch {
        if (!options.silent && isCurrentRoute(teamId)) {
            error.value = 'Team not found.'
        }
    } finally {
        inFlight = false
    }
}

/**
 * The KPIs load beside the team, never inside its load: a KPI read that fails (or hangs) leaves the charter
 * and the task graph standing, and never holds up the silent refresh those poll on. Readings move on their
 * own cadence — days, not seconds — so this reads once per team rather than on every poll.
 */
async function loadKpis(): Promise<void> {
    const teamId = String(route.params.id)
    try {
        const tree = await api.teamKpis(teamId)
        if (isCurrentRoute(teamId)) {
            kpiTree.value = tree
        }
    } catch {
        kpiTree.value = null
    }
}

/**
 * The charter's KPI targets load beside the team too, once per team rather than on every poll: a failed read
 * leaves the charter and the task graph standing, and an edit in progress is never replaced by a refresh.
 */
async function loadKpiTargets(): Promise<void> {
    const teamId = String(route.params.id)
    try {
        const { kpis } = await api.teamKpiTargets(teamId)
        if (isCurrentRoute(teamId)) {
            charterKpis.value = kpis
            storedKpis = JSON.stringify(kpis)
            savedKpis.value = kpis.map(kpi => ({ ...kpi }))
        }
    } catch {
        if (isCurrentRoute(teamId)) {
            charterKpisFailed.value = true
        }
    }
}

/**
 * The cloner's memory grant on this team (#1841), per layer: whether its cloned tasks may teach the team's and the
 * enterprise's memory. Read once per team beside the team; a failed read hides the section and nothing else.
 */
const memoryGrants = ref<TeamMemoryGrants | null>(null)
const memoryGrantError = ref('')
const memoryGrantNotice = ref('')
const withdrawingLayer = ref<MemoryGrantLayer | null>(null)
type MemoryGrantLayer = 'team' | 'enterprise'
const MEMORY_GRANT_ROWS: ReadonlyArray<{ layer: MemoryGrantLayer; label: string }> = [
    { layer: 'team', label: "This team's memory" },
    { layer: 'enterprise', label: "Your enterprise's memory" },
]

async function loadMemoryGrants(): Promise<void> {
    const teamId = String(route.params.id)
    memoryGrantNotice.value = ''
    try {
        const grants = await api.teamMemoryGrants(teamId)
        if (isCurrentRoute(teamId)) {
            memoryGrants.value = grants
        }
    } catch {
        if (isCurrentRoute(teamId)) {
            memoryGrants.value = null
        }
    }
}

function memoryGrantText(layer: MemoryGrantLayer): string {
    const grant = memoryGrants.value?.[layer]
    if (!grant?.granted) {
        return grant?.withdrawnAt
            ? `Not granted (withdrawn ${new Date(grant.withdrawnAt).toLocaleDateString()})`
            : 'Not granted'
    }
    return 'Allowed'
}

/** What a Withdraw button does, in words a screen reader can say: the layer it withdraws, not just "Withdraw". */
function memoryGrantWithdrawLabel(layer: MemoryGrantLayer): string {
    const row = MEMORY_GRANT_ROWS.find(candidate => candidate.layer === layer)
    return `Withdraw the grant for ${row ? row.label.toLowerCase() : 'memory'}`
}

/**
 * Withdraws one layer: the next write there from this team's cloned tasks is refused. Notes already written stay.
 * Nothing grants a layer again (#1841 decision 11), so it asks first and says so, as the other one-way actions do.
 */
async function withdrawMemoryGrant(layer: MemoryGrantLayer): Promise<void> {
    if (
        !confirm(
            `${memoryGrantWithdrawLabel(layer)}? This team's cloned tasks stop adding notes there from their next note, and you cannot grant it again. Notes already written stay.`,
        )
    ) {
        return
    }
    const teamId = String(route.params.id)
    withdrawingLayer.value = layer
    memoryGrantError.value = ''
    memoryGrantNotice.value = ''
    try {
        const grants = await api.withdrawTeamMemoryGrant(teamId, layer)
        if (isCurrentRoute(teamId)) {
            memoryGrants.value = grants
            memoryGrantNotice.value = 'Withdrawn. Notes already written stay.'
        }
    } catch {
        // A failure that lands after the page moved to another team is not that team's error.
        if (isCurrentRoute(teamId)) {
            memoryGrantError.value = 'The grant could not be withdrawn. Try again.'
        }
    } finally {
        withdrawingLayer.value = null
    }
}

/** What sharing would publish, and whether the team is shared already, read beside the team (#1844). */
async function loadShare(): Promise<void> {
    const teamId = String(route.params.id)
    try {
        const review = await api.teamShareReview(teamId)
        if (isCurrentRoute(teamId)) {
            share.value = review
        }
    } catch {
        if (isCurrentRoute(teamId)) {
            share.value = null
        }
    }
}

/** The dialog shared the team: its review is the latest, and the page says so. */
function onShared(review: TeamShareReview): void {
    share.value = review
    shareNotice.value = review.shared ? `Shared to the catalog as ${review.shared.name}.` : 'Shared to the catalog.'
}

/** Where the Catalog lists this team, searched by its catalog name, since the Catalog has no page per entry. */
const catalogHref = computed(() => {
    const entry = share.value?.shared
    return entry ? `/catalog?kind=teams&search=${encodeURIComponent(entry.name)}` : '/catalog?kind=teams'
})

onMounted(() => {
    void load()
    void loadKpis()
    void loadKpiTargets()
    void loadMemoryGrants()
    void loadShare()
})

// The `/teams/:id` route reuses this same component instance across a `t1` → `t2` navigation (no
// `:key` forces a remount), so `concurrencyInitialized` guarding the pre-fill in `load()` above
// would otherwise never flip back to `false` — leaving `t1`'s concurrent-run limit showing (and,
// if the form is submitted unedited, PATCHed onto `t2`) after switching teams.
watch(
    () => route.params.id,
    () => {
        concurrencyInitialized.value = false
        concurrentRunLimitInput.value = ''
        concurrencyError.value = null
        enabledError.value = null
        kpiTree.value = null
        signOffNotice.value = ''
        signOffError.value = ''
        charterKpis.value = null
        charterKpisFailed.value = false
        storedKpis = ''
        savedKpis.value = []
        // The previous team's memory grant must not show, or decide the Withdraw buttons, under this team's name.
        memoryGrants.value = null
        memoryGrantError.value = ''
        memoryGrantNotice.value = ''
        share.value = null
        shareOpen.value = false
        shareNotice.value = ''
        void load()
        void loadKpis()
        void loadKpiTargets()
        void loadMemoryGrants()
        void loadShare()
    },
)

useVisiblePolling(() => void load({ silent: true }))

async function save(): Promise<void> {
    if (!team.value || !charter.value) {
        return
    }
    saved.value = false
    error.value = null
    try {
        // The KPIs go first, so a list the API refuses keeps the goal unsaved with it and the reason shown.
        if (charterKpis.value && JSON.stringify(charterKpis.value) !== storedKpis) {
            charterKpis.value = (await api.setTeamKpiTargets(team.value.id, charterKpis.value)).kpis
            storedKpis = JSON.stringify(charterKpis.value)
            savedKpis.value = charterKpis.value.map(kpi => ({ ...kpi }))
            // What was set decides what is counted, so the lights beneath are read again.
            void loadKpis()
        }
        // Only the goal is sent: the patch merges it over the stored charter, whose other answers stay as they are.
        team.value = await api.patchTeam(team.value.id, { charter: { purpose: charter.value.purpose } })
        charter.value = { ...emptyCharter(), ...team.value.charter }
        charterDraft.clear()
        saved.value = true
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not save (${caught.message}).` : 'Could not save.'
    }
}

/**
 * The team's own concurrent-run override, sent alone so it never carries the charter or lead along.
 * A blank field sends `null` (inherit the enterprise limit), never `0` (which would mean "never run").
 */
async function saveConcurrency(): Promise<void> {
    if (!team.value) {
        return
    }
    concurrencyError.value = null
    try {
        const concurrentRunLimit = parseConcurrentRunLimit(concurrentRunLimitInput.value)
        team.value = await api.patchTeam(team.value.id, { concurrentRunLimit })
        concurrentRunLimitInput.value = formatConcurrentRunLimit(team.value.concurrentRunLimit)
    } catch (caught) {
        concurrencyError.value = caught instanceof Error ? caught.message : 'Could not save.'
    }
}

/**
 * Switch the team off or on in one request (#1810), as the Spend page's switch does. The switch never flips on its
 * own: it shows what the server answered, so a failure leaves the previous state standing with the reason beside it.
 */
async function toggleTeam(): Promise<void> {
    if (!team.value || toggling.value) {
        return
    }
    const teamId = team.value.id
    toggling.value = true
    enabledError.value = null
    try {
        const answered = await api.setTeamEnabled(teamId, !teamOn.value)
        // An answer that lands after the page moved to another team is not that team's state.
        if (isCurrentRoute(teamId)) {
            team.value = answered
        }
    } catch (caught) {
        if (isCurrentRoute(teamId)) {
            enabledError.value =
                caught instanceof Error
                    ? `Could not switch the team (${caught.message}).`
                    : 'Could not switch the team.'
        }
    } finally {
        toggling.value = false
    }
}

async function remove(): Promise<void> {
    if (!team.value) {
        return
    }
    deleting.value = true
    error.value = null
    try {
        await api.deleteTeam(team.value.id)
        await router.push('/teams')
    } catch (caught) {
        const code = caught instanceof Error ? caught.message : ''
        error.value = code === 'last_team' ? 'Cannot delete the last team.' : 'Could not delete team.'
    } finally {
        deleting.value = false
    }
}

function openTask(taskId: string) {
    void router.push(`/tasks/${taskId}`)
}

const teamMembers = computed(() => team.value?.members ?? [])
const availableMembers = computed(() => {
    const held = new Set(teamMembers.value.map(member => member.id))
    return people.value.filter(member => !held.has(member.id))
})

/** A member row carries the joined strings the table searches, sorts and filters on, as on /members. */
type MemberRow = Member & { roleNames: string; leadNames: string }

const memberRows = computed<MemberRow[]>(() =>
    teamMembers.value.map(member => ({
        ...member,
        roleNames: member.roles.map(role => role.name).join(', '),
        leadNames: member.leadOf.join(', '),
    })),
)

const memberTable = useClientTable({
    data: memberRows,
    searchFields: ['name', 'email', 'persona', 'roleNames', 'leadNames'],
    defaultSort: 'name',
    defaultOrder: 'ASC',
})
/**
 * #633: the members table's search, sort and filters come back on the next visit to this team's page, kept per team
 * (as the goal draft is): a search on one team's members never narrows another team's.
 */
const memberList = useListState(`team-members:${String(route.params.id)}`, memberTable.listFields)
// `/teams/t1` → `/teams/t2` reuses this page (see the watcher on `route.params.id` above): the members table then shows
// t2's own saved choices, from its first page, and t1's stay as they were.
watch(
    () => route.params.id,
    id => {
        memberList.rekey(`team-members:${String(id)}`)
        memberTable.page.value = 1
    },
)

const memberColumns: DataTableColumn[] = [
    { key: 'name', label: 'Member', sort: 'name', type: 'text' },
    { key: 'email', label: 'Email', sort: 'email', type: 'text' },
    { key: 'roleNames', label: 'Roles', sort: 'roleNames', type: 'text' },
    { key: 'leadNames', label: 'Leads', sort: 'leadNames', type: 'text', defaultVisible: false },
    { key: 'persona', label: 'Persona', filterable: false, class: 'max-w-sm', defaultVisible: false },
    { key: 'actions', label: 'Actions', filterable: false, class: 'text-right' },
]

function openMember(row: Record<string, unknown>): void {
    void router.push(`/members/${String(row.id)}`)
}

async function applyTeam(change: () => Promise<TeamDetail>): Promise<void> {
    busy.value = true
    error.value = null
    try {
        team.value = await change()
    } catch (caught) {
        error.value = caught instanceof Error ? caught.message : 'Could not update team.'
    } finally {
        busy.value = false
    }
}

/**
 * Picking a member adds them, with no second step. Roles are held independently of teams, so the role
 * picker that used to sit beside this one was only a shortcut to what /members/:id already does.
 *
 * The choice arrives from the kit Select's model update; the picker is never left holding it.
 */
async function addMember(choice: unknown): Promise<void> {
    const memberId = choice === null || choice === undefined ? '' : String(choice)
    const teamId = team.value?.id
    pickMember.value = ''
    if (!memberId || !teamId) {
        return
    }
    await applyTeam(() => api.addTeamMember(teamId, memberId, null))
}

async function removeMember(memberId: unknown): Promise<void> {
    const teamId = team.value?.id
    if (!teamId) {
        return
    }
    await applyTeam(() => api.removeTeamMember(teamId, String(memberId)))
}

/** The lead, chosen through the kit Select's model update: a member's id, or `null` when the COO stands in. */
async function setLead(choice: unknown): Promise<void> {
    const teamId = team.value?.id
    if (!teamId) {
        return
    }
    await applyTeam(() => api.patchTeam(teamId, { leadMemberId: leadMemberIdOf(choice) }))
}
</script>

<template>
	<section class="w-full space-y-6 p-4 md:p-8">
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<template v-else-if="team && charter">
			<div class="flex items-start justify-between gap-4">
				<div>
					<!-- The team's on/off switch sits in the title row, as Spend's does (#1810). -->
					<div class="flex flex-wrap items-center gap-4">
						<ZhPageTitle kicker="Team">{{ team.name }}</ZhPageTitle>
						<div class="space-y-1">
							<!-- The words either side are part of the control: clicking Off or On flips the switch too. -->
							<label class="inline-flex shrink-0 items-center gap-2 text-sm">
								<span class="text-muted-foreground">Off</span>
								<Switch
									:model-value="teamOn"
									aria-label="Team"
									:disabled="toggling"
									data-testid="team-enabled"
									@update:model-value="toggleTeam"
								/>
								<span class="text-muted-foreground">On</span>
							</label>
							<!-- Who last switched it, and when (#1812). -->
							<p v-if="statusChangeLine" class="text-muted-foreground text-xs" data-testid="team-status-change">
								{{ statusChangeLine }}
							</p>
						</div>
					</div>
					<p v-if="enabledError" class="text-destructive text-sm" role="alert">{{ enabledError }}</p>
					<p class="text-muted-foreground text-sm">{{ team.slug }}</p>
					<p v-if="team.charter.purpose" class="mt-1 text-sm" data-testid="team-goal-line">
						<span class="text-muted-foreground">Goal ·</span> {{ team.charter.purpose }}
					</p>
					<p class="mt-1 flex flex-wrap items-center gap-2 text-sm">
						<ZhSystemLabel v-if="team.leadName" as="span">Lead · {{ team.leadName }}</ZhSystemLabel>
						<ZhSystemLabel v-else-if="team.cooActingAsLead" as="span" tone="system">COO acting as lead</ZhSystemLabel>
						<!-- Shared to the catalog (#1844): the entry under this team's slug, where any company can clone it. -->
						<template v-if="share?.shared">
							<ZhSystemLabel as="span" tone="system" data-testid="team-shared">Shared to the catalog</ZhSystemLabel>
							<RouterLink class="underline-offset-2 hover:underline" :to="catalogHref" data-testid="team-shared-link">
								View in Catalog
							</RouterLink>
							<!-- What the entry is behind on: roles added or removed, and each role's own listing changes. -->
							<ZhSystemLabel v-if="(share.changes ?? 0) > 0" as="span" tone="system" data-testid="team-share-changes">
								{{ share.changes }} change{{ share.changes === 1 ? '' : 's' }} not in the catalog
							</ZhSystemLabel>
						</template>
					</p>
					<p v-if="shareNotice" class="mt-1 text-sm" role="status" data-testid="team-share-notice">{{ shareNotice }}</p>
				</div>
				<div class="flex shrink-0 items-center gap-2">
					<!-- Share publishes the team and the roles chosen; once shared, the same button brings the entry up to date. -->
					<Button
						type="button"
						size="sm"
						variant="outline"
						data-testid="share-team"
						@click="shareOpen = true"
					>
						{{ share?.shared ? 'Update catalog' : 'Share' }}
					</Button>
					<Button
						type="button"
						size="sm"
						variant="outline"
						data-testid="edit-team"
						:aria-expanded="editing"
						aria-controls="team-goal"
						@click="editing = !editing"
					>
						{{ editing ? 'Done editing' : 'Edit team' }}
					</Button>
					<ZhConfirmButton
						label="Delete team"
						:title="`Delete ${team.name}?`"
						description="The team, its goal, its KPIs and its tool bindings go. Members and their roles are not deleted, but they stop sitting on this team."
						:disabled="deleting"
						@confirm="remove"
					/>
				</div>
			</div>
			<ZhPanel
				v-if="editing"
				id="team-goal"
				measure="prose"
				class="space-y-4"
				aria-labelledby="team-goal-heading"
			>
				<ZhSectionHeading id="team-goal-heading">Goal and KPIs</ZhSectionHeading>
				<!-- One sentence: the line the team's page, the catalog and the public site show (#1844). -->
				<label class="block space-y-1">
					<span class="block text-sm font-medium">Goal</span>
					<span class="text-muted-foreground block text-xs">What this team is for, in one sentence.</span>
					<Textarea v-model="charter.purpose" rows="2" data-testid="team-goal-input" />
				</label>
				<!-- The team's KPIs (#895), each with a target, read and saved through their own endpoint. -->
				<div class="space-y-2" data-testid="team-goal-kpis">
					<span class="block text-sm font-medium">KPIs</span>
					<span class="text-muted-foreground block text-xs">
						Each with a target, an AMBER band, a cadence and a source. At most one is the key KPI.
					</span>
					<KpiListEditor v-if="charterKpis" v-model="charterKpis" key-kpi />
					<p v-else-if="charterKpisFailed" class="text-muted-foreground text-sm" data-testid="team-goal-kpis-unread">
						The team's KPIs could not be read, so saving leaves them as they are.
					</p>
					<p v-else class="text-muted-foreground text-sm">Reading the team's KPIs…</p>
				</div>
				<Button type="button" data-testid="save-team-goal" @click="save">Save</Button>
				<p v-if="saved" class="text-sm text-muted-foreground">Saved.</p>
			</ZhPanel>
			<TeamShareDialog :team-id="team.id" :open="shareOpen" @update:open="open => (shareOpen = open)" @shared="onShared" />
			<ZhPanel class="space-y-3" data-testid="team-kpi-tree" aria-labelledby="team-kpis-heading">
				<div>
					<ZhSectionHeading id="team-kpis-heading">KPIs</ZhSectionHeading>
					<p class="text-muted-foreground text-sm">
						Every KPI this team carries, with its roles' and their tasks' KPIs beneath them. At risk, off
						target and overdue first.
					</p>
				</div>
				<!--
					KPIs nobody has signed off yet are on the tree, marked, and counted here: until a person signs them off no run
					sees them, so a scheduled task feeding one records nothing. One button signs off every one that can be.
				-->
				<div
					v-if="pendingKpiCount > 0"
					class="flex flex-wrap items-center justify-between gap-2"
					role="status"
					data-testid="team-kpis-pending"
				>
					<p class="text-sm">
						{{ pendingKpiCount }} KPI{{ pendingKpiCount === 1 ? ' is' : 's are' }} waiting for sign-off. No run sees
						{{ pendingKpiCount === 1 ? 'it' : 'them' }} or records a reading until a person signs
						{{ pendingKpiCount === 1 ? 'it' : 'them' }} off.
					</p>
					<Button type="button" size="sm" :disabled="signingOff" data-testid="team-kpis-sign-off" @click="signOffAll">
						Sign off all
					</Button>
				</div>
				<p v-if="signOffNotice" class="text-sm" role="status" data-testid="team-kpis-sign-off-notice">{{ signOffNotice }}</p>
				<p v-if="signOffError" class="text-destructive text-sm" role="alert">{{ signOffError }}</p>
				<!--
					The team's own KPIs as cards: what was set, how each is doing, and the form a person enters a
					reading with. The plain rows stand in only when what was set could not be read.
				-->
				<KpiTargetCards
					v-if="savedKpis.length"
					data-testid="team-kpi-cards"
					:kpis="savedKpis"
					@recorded="loadKpis"
				/>
				<ul v-else-if="teamKpis.length" class="space-y-1">
					<li v-for="kpi in teamKpis" :key="kpi.id" class="flex flex-wrap items-center gap-2 text-sm">
						<ZhKpiLight v-if="kpi.rag" :rag="kpi.rag" />
						<span class="font-medium">{{ kpi.name }}</span>
						<ZhSystemLabel v-if="kpi.pending" as="span" tone="system" data-testid="kpi-row-pending">Waiting for sign-off</ZhSystemLabel>
						<span v-else class="text-muted-foreground text-xs">{{ readingNote(kpi) }}</span>
					</li>
				</ul>
				<ZhEmptyState v-else label="No team KPIs">No KPIs on the team itself.</ZhEmptyState>
				<!-- Roles, then their tasks, step in beneath the team: the indent alone carries the nesting. -->
				<ul v-if="roleKpis.length" class="space-y-3">
					<li v-for="role in roleKpis" :key="role.roleId" class="space-y-1 pl-4">
						<p class="text-sm font-medium">{{ role.roleName }}</p>
						<ul class="space-y-1">
							<li v-for="kpi in role.kpis" :key="kpi.id" class="flex flex-wrap items-center gap-2 text-sm">
								<ZhKpiLight v-if="kpi.rag" :rag="kpi.rag" />
								<span>{{ kpi.name }}</span>
								<ZhSystemLabel v-if="kpi.pending" as="span" tone="system" data-testid="kpi-row-pending">Waiting for sign-off</ZhSystemLabel>
								<span v-else class="text-muted-foreground text-xs">{{ readingNote(kpi) }}</span>
							</li>
						</ul>
						<ul class="space-y-1">
							<li v-for="task in role.tasks" :key="task.taskId" class="space-y-1 pl-4">
								<RouterLink class="text-sm underline-offset-2 hover:underline" :to="`/tasks/${task.taskId}`">
									{{ task.taskName }}
								</RouterLink>
								<ul class="space-y-1">
									<li
										v-for="kpi in task.kpis"
										:key="kpi.id"
										class="flex flex-wrap items-center gap-2 text-sm"
									>
										<ZhKpiLight v-if="kpi.rag" :rag="kpi.rag" />
										<span>{{ kpi.name }}</span>
										<ZhSystemLabel v-if="kpi.pending" as="span" tone="system" data-testid="kpi-row-pending">Waiting for sign-off</ZhSystemLabel>
										<span v-else class="text-muted-foreground text-xs">{{ readingNote(kpi) }}</span>
									</li>
								</ul>
							</li>
						</ul>
					</li>
				</ul>
			</ZhPanel>
			<ZhPanel class="space-y-3" aria-labelledby="team-workflow-heading">
				<div>
					<ZhSectionHeading id="team-workflow-heading">Task workflow</ZhSectionHeading>
					<p class="text-muted-foreground text-sm">Explore the team's tasks (the tasks of the roles its members hold), their latest runs, and immediate incoming and outgoing handoffs.</p>
				</div>
				<!-- The graph's frame is a surface of its own; the kit gives a canvas its height. -->
				<ZhPanel v-if="workflowTasks.length" as="div" flush canvas data-testid="team-task-graph">
					<TaskGraph :tasks="workflowTasks" :related-tasks="team.relatedTasks" scope="team" @select="openTask" />
				</ZhPanel>
				<ZhEmptyState v-else label="No tasks">No tasks belong to this team.</ZhEmptyState>
			</ZhPanel>
			<ZhPanel class="space-y-3" data-testid="team-lead" aria-labelledby="team-lead-heading">
				<ZhSectionHeading id="team-lead-heading">Team lead</ZhSectionHeading>
				<!-- `aria-label` lands on the field the kit Select mirrors its choice on; the heading names the trigger. -->
				<Select
					:model-value="leadChoice(team.leadMemberId)"
					:disabled="busy"
					aria-label="Team lead"
					@update:model-value="setLead"
				>
					<SelectTrigger class="w-full max-w-sm" aria-labelledby="team-lead-heading">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem :value="COO_ACTING_AS_LEAD">COO acting as lead</SelectItem>
						<SelectItem v-for="person in people" :key="person.id" :value="person.id">{{ memberLabel(person) }}</SelectItem>
					</SelectContent>
				</Select>
			</ZhPanel>
			<ZhPanel
				measure="form"
				class="space-y-3"
				data-testid="team-concurrency-section"
				aria-labelledby="team-concurrency-heading"
			>
				<ZhSectionHeading id="team-concurrency-heading">Concurrent runs</ZhSectionHeading>
				<p v-if="concurrencyError" class="text-destructive text-sm">{{ concurrencyError }}</p>
				<form data-testid="team-concurrency-form" class="flex items-center gap-2" @submit.prevent="saveConcurrency">
					<Input
						v-model="concurrentRunLimitInput"
						data-testid="team-concurrency"
						type="number"
						min="1"
						aria-labelledby="team-concurrency-heading"
						placeholder="Inherits the enterprise limit when blank"
					/>
					<Button type="submit" size="sm" variant="outline">Save</Button>
				</form>
				<p class="text-muted-foreground text-xs">
					Leave blank to draw on the enterprise's free capacity; set a number to hold that many for this team.
					One active run per member still applies regardless of this limit.
				</p>
			</ZhPanel>
			<ZhPanel
				v-if="memoryGrants"
				class="space-y-3"
				data-testid="team-memory-grant"
				aria-labelledby="team-memory-grant-heading"
			>
				<ZhSectionHeading id="team-memory-grant-heading">Memory grant</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">
					Tasks cloned onto this team add what they learn to your enterprise's memory, as tasks your enterprise wrote
					itself do. Withdraw a layer to stop them; it applies only to cloned tasks. Withdrawing stops the next note,
					even in a run already going, and cannot be undone; notes already written stay.
				</p>
				<p v-if="memoryGrantError" class="text-destructive text-sm">{{ memoryGrantError }}</p>
				<p v-if="memoryGrantNotice" class="text-sm" role="status">{{ memoryGrantNotice }}</p>
				<div
					v-for="row in MEMORY_GRANT_ROWS"
					:key="row.layer"
					:data-testid="`memory-grant-${row.layer}`"
					class="flex items-center gap-2 text-sm"
				>
					<span class="font-medium">{{ row.label }}</span>
					<ZhSystemLabel tone="muted">{{ memoryGrantText(row.layer) }}</ZhSystemLabel>
					<Button
						v-if="memoryGrants[row.layer].granted"
						type="button"
						size="sm"
						variant="outline"
						:disabled="withdrawingLayer !== null"
						:aria-label="memoryGrantWithdrawLabel(row.layer)"
						@click="withdrawMemoryGrant(row.layer)"
					>Withdraw</Button>
				</div>
			</ZhPanel>
			<ZhPanel class="space-y-3" data-testid="team-members" aria-labelledby="team-members-heading">
				<ZhSectionHeading id="team-members-heading">Team members</ZhSectionHeading>
				<!-- Picking a member adds them at once, so the picker never holds one: it always reads "Add member…". -->
				<template v-if="availableMembers.length">
					<span id="team-add-member-label" class="sr-only">Add member</span>
					<Select
						:model-value="pickMember"
						:disabled="busy"
						aria-label="Add member"
						@update:model-value="addMember"
					>
						<SelectTrigger class="w-full max-w-sm" aria-labelledby="team-add-member-label">
							<SelectValue placeholder="Add member…" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem v-for="person in availableMembers" :key="person.id" :value="person.id">
								{{ memberLabel(person) }}
							</SelectItem>
						</SelectContent>
					</Select>
				</template>
				<DataTable
					storage-key="team-members"
					:columns="memberColumns"
					:list-state="memberList"
					v-bind="memberTable.tableProps.value"
					@row-click="openMember"
				>
					<template #cell-name="{ row }">
						<RouterLink
							:to="`/members/${row.id}`"
							class="flex items-center gap-2 font-medium underline-offset-2 hover:underline"
						>
							<MemberAvatar :member="(row as unknown as Member)" class="size-8" />
							<span>{{ memberLabel(row as unknown as Member) }}</span>
						</RouterLink>
					</template>
					<template #cell-roleNames="{ row }">
						<span v-if="(row as unknown as Member).roles.length" class="flex flex-wrap gap-1">
							<ZhSystemLabel v-for="role in (row as unknown as Member).roles" :key="role.id" as="span">
								{{ role.name }}
							</ZhSystemLabel>
						</span>
						<span v-else class="text-muted-foreground">No role</span>
					</template>
					<template #cell-leadNames="{ row }">
						<span v-if="(row as unknown as Member).leadOf.length" class="flex flex-wrap gap-1">
							<ZhSystemLabel v-for="teamName in (row as unknown as Member).leadOf" :key="teamName" as="span">
								Lead · {{ teamName }}
							</ZhSystemLabel>
						</span>
						<span v-else class="text-muted-foreground">—</span>
					</template>
					<template #cell-actions="{ row }">
						<Button
							type="button"
							size="sm"
							variant="outline"
							:disabled="busy"
							:aria-label="`Remove ${memberLabel(row as unknown as Member)}`"
							@click.stop="removeMember(row.id)"
						>
							Remove
						</Button>
					</template>
				</DataTable>
			</ZhPanel>
			<ZhPanel class="space-y-3" aria-labelledby="team-agents-heading">
				<ZhSectionHeading id="team-agents-heading">Team model connections</ZhSectionHeading>
				<AgentConnectionsPanel layer="team" :layer-id="team.id" />
			</ZhPanel>
			<ZhPanel class="space-y-3" aria-labelledby="team-tools-heading">
				<ZhSectionHeading id="team-tools-heading">Team tools</ZhSectionHeading>
				<ToolBindings lock-layer="team" :lock-layer-id="team.id" />
			</ZhPanel>
		</template>
	</section>
</template>
